import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { money, type Money } from '@/lib/money'
import type { Account } from '@/data/types'
import { Sidebar } from '@/app/navigation/Sidebar'
import { MobileTabs } from '@/app/navigation/MobileTabs'
import { Topbar } from '@/app/layouts/Topbar'
import { RateTicker } from '@/components/RateTicker'
import styles from './AppLayout.module.css'

/**
 * Totals are shown in USD. Balances in other currencies are converted with an
 * indicative rate — the real client will get this from the pricing service.
 */
const INDICATIVE_USD_RATE: Record<string, number> = {
  USD: 1,
  USDC: 1,
  EUR: 1.08,
  MXN: 1 / 17.42,
  BRL: 1 / 5.34,
  COP: 1 / 3920,
  BOB: 1 / 6.91,
  GBP: 1.27,
}

const totalInUsd = (accounts: readonly Account[]): Money =>
  money(
    accounts.reduce(
      (sum, account) => sum + account.balance.minorUnits * (INDICATIVE_USD_RATE[account.currency] ?? 0),
      0,
    ),
    'USD',
  )

export function AppLayout() {
  const repository = useRepository()
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  const user = useQuery(() => repository.getUser(), [repository])
  const accounts = useQuery(() => repository.listAccounts(), [repository])

  useEffect(() => setMenuOpen(false), [pathname])
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  const total = accounts.data ? totalInUsd(accounts.data) : undefined
  const sidebar = (
    <Sidebar
      totalBalance={total}
      changeFraction={0.024}
      onClose={menuOpen ? () => setMenuOpen(false) : undefined}
    />
  )

  return (
    <div className={styles.shell}>
      <aside className={styles.rail}>{sidebar}</aside>

      {menuOpen && (
        <div className={styles.drawer} role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            className={styles.scrim}
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
          <div className={styles.panel}>{sidebar}</div>
        </div>
      )}

      <div className={styles.main}>
        <Topbar user={user.data} onOpenMenu={() => setMenuOpen(true)} />
        <main className={styles.content}>
          <RateTicker className={styles.ticker} />
          <Outlet />
        </main>
      </div>

      <MobileTabs />
    </div>
  )
}
